import type { AppKey, UserDto } from '@iac/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  onUnauthenticated,
  refreshAccessToken,
  setAccessToken,
} from './api';

interface Session {
  user: UserDto;
  apps: AppKey[];
  version: string;
}

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthValue {
  status: Status;
  session: Session | null;
  /** Resolves to an error message, or null on success. */
  signIn: (login: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  /** Replaces the session's user after the API returned a fresh copy. */
  setUser: (user: UserDto) => void;
  hasApp: (app: AppKey) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session | null>(null);

  const loadSession = useCallback(async () => {
    const res = await api.auth.me();
    if (res.status === 200) {
      setSession(res.body);
      setStatus('authenticated');
      return true;
    }
    setSession(null);
    setStatus('anonymous');
    return false;
  }, []);

  /**
   * On a cold load there is no access token in memory — a reload throws it away
   * — but the refresh cookie may still be good. Try to trade it for a token
   * before deciding the user is anonymous, otherwise every refresh of the page
   * would bounce a signed-in user to the login screen.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const refreshed = await refreshAccessToken();
      if (cancelled) return;
      if (!refreshed) {
        setStatus('anonymous');
        return;
      }
      await loadSession();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  useEffect(
    () =>
      onUnauthenticated(() => {
        setSession(null);
        setStatus('anonymous');
      }),
    [],
  );

  const signIn = useCallback<AuthValue['signIn']>(
    async (login, password) => {
      const res = await api.auth.login({ body: { login, password } });
      if (res.status !== 200) return 'Invalid login or password.';
      setAccessToken(res.body.accessToken);
      setSession({
        user: res.body.user,
        apps: res.body.apps,
        // `login` does not carry the release version; `me` does.
        version: '',
      });
      setStatus('authenticated');
      void loadSession();
      return null;
    },
    [loadSession],
  );

  const signOut = useCallback(async () => {
    await api.auth.logout();
    setAccessToken(null);
    setSession(null);
    setStatus('anonymous');
  }, []);

  const setUser = useCallback<AuthValue['setUser']>((user) => {
    setSession((current) => (current ? { ...current, user } : current));
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      session,
      signIn,
      signOut,
      setUser,
      hasApp: (app) => session?.apps.includes(app) ?? false,
    }),
    [status, session, signIn, signOut, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
